import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useCallback, useEffect } from 'react';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { useChannelsStore } from '@/components/pages/channels/use-channels-store';
import { AggregateDataCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Skeleton } from '@/components/ui/skeleton';
import { Title } from '@/components/ui/title';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { useOpeningContext } from '@/contexts/opening-context';
import { useCampaignRefetch } from '@/hooks/use-campaign-refetch';
import { useHireUrl } from '@/hooks/use-hire-url';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { HiringGoal } from './hiring-goal';
import { HiringOpeningsPopover } from './hiring-openings-popover';
import { Tabs } from './tabs';
import { useEditTargets } from './use-edit-targets';

function OverviewContent() {
  const openingSlug = useOpeningSlug();
  const setOpening = useChannelsStore(s => s.setOpening);
  const {
    showAggregateData,
    setShowAggregateData,
    setAnalyticsData,
    showCheckbox,
    setHiringGoal,
  } = useOpeningContext();
  const { setRefetchFn: setEditTargetsRefetchFn } = useEditTargetsContext();
  const { getHireATHiringGoalUrl } = useHireUrl();

  const { data: funnelData, refetch } = useSuspenseQuery({
    queryKey: ['openingDetail', openingSlug, showAggregateData],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpenings(
        openingSlug,
        showAggregateData,
      ),
  });

  const refetchFn = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useCampaignRefetch(refetchFn);

  useEffect(() => {
    setOpening({ id: funnelData.opening_id, title: funnelData.title });
    setAnalyticsData(funnelData.analytics);
    setEditTargetsRefetchFn(refetchFn);
  }, [
    funnelData,
    setOpening,
    setAnalyticsData,
    setEditTargetsRefetchFn,
    refetchFn,
  ]);

  useEditTargets(funnelData);

  const {
    active,
    title,
    sourcing_status: sourcingStatus,
    hiring_goal: hiringGoal,
  } = funnelData;

  const performanceStatusMetrics = hiringGoal?.performance_status_metrics;
  const { status, criteria } = performanceStatusMetrics ?? {};

  useEffect(() => {
    if (hiringGoal) {
      setHiringGoal(hiringGoal);
    }
  }, [hiringGoal, setHiringGoal]);

  return (
    <div className="shadow-base flex flex-col gap-4 rounded border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex flex-col items-start gap-6 py-1.5 lg:flex-row lg:justify-between lg:gap-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <OpeningStatus active={active} />
            <Title title={title} />
          </div>
          <SourcingStatusPill status={sourcingStatus} />
          {status && (
            <PerformanceStatusPill
              status={status}
              criteria={criteria ?? null}
              expected={
                (criteria === 'hire'
                  ? performanceStatusMetrics?.expected_hires_count
                  : performanceStatusMetrics?.expected_applicants_count) ?? null
              }
              actual={
                (criteria === 'hire'
                  ? funnelData.analytics.hire_stats.actual
                  : funnelData.analytics.applicants_stats.actual) ?? null
              }
            />
          )}
        </div>
        <HiringGoal
          hiringGoal={hiringGoal}
          hireATHiringGoalUrl={getHireATHiringGoalUrl(
            funnelData.brand_slug ?? 'no_brand',
            funnelData.location_id,
            funnelData.opening_id,
          )}
        />
      </div>

      <div className="flex items-center gap-6">
        <HiringOpeningsPopover />
        {showCheckbox && (
          <AggregateDataCheckbox
            checked={showAggregateData}
            onCheckedChange={value => setShowAggregateData(!!value)}
          />
        )}
      </div>

      <Tabs />
    </div>
  );
}

export function Overview() {
  return (
    <Suspense fallback={<Overview.Skeleton />}>
      <OverviewContent />
    </Suspense>
  );
}

Overview.Skeleton = function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-4 border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          <Title.Skeleton />
          <SourcingStatusPill.Skeleton />
          <PerformanceStatusPill.Skeleton />
        </div>
        <HiringGoal.Skeleton />
      </div>
      <div className="flex items-center gap-6">
        <HiringOpeningsPopover.Skeleton />
        <Skeleton className="h-5 w-[178px]" />
      </div>
      <Tabs />
    </div>
  );
};
